// Ported from apps/webapp/src/components/gantt-chart/components/draggable-strip.tsx:129-250.
// Positioned in the DAY domain (spec §6.1): `--bar-offset` / `--bar-span` are day counts, and CSS
// does the multiplication by `--gantt-px-per-day` — a zoom change is one variable write, not a
// re-render of every bar. `layout.x` / `layout.width` (pixel domain) are NOT used here; they exist
// for Task 13's off-screen indicators, which compare directly against `scrollLeft`.
//
// TWO OFFSET PROPERTIES, NOT ONE, and this is the whole of the fix for "the dragged bar
// disappears". `--bar-offset` is written by `row-list.tsx` on the ancestor `.track` from the item's
// own un-dragged span; `--bar-offset-delta` is written by `bar.tsx` on the BAR ITSELF, and only
// while an interaction is previewing. They have to be separate properties because a declaration
// cannot read the inherited value of the property it is declaring: `--bar-offset:
// calc(var(--bar-offset) + 2)` on the bar is a self-reference, and per CSS Variables §3.2 every
// custom property in a cycle computes to the guaranteed-invalid value — so `left` below became
// invalid at computed-value time, fell back to its initial `auto`, and an absolutely-positioned bar
// landed at the track's static origin (day 0 of a ~2-year track, thousands of pixels off-screen).
// Measured in Chromium: computed `--bar-offset` `""` and `left` `0px`, against `"10"` / `480px` for
// a sibling that merely inherits. The `, 0` fallback is what makes a non-previewing bar resolve to
// exactly the same `left` it did before this split existed (verified: 480px either way).
.bar {
  position: absolute;
  top: 50%;
  left: calc((var(--bar-offset) + var(--bar-offset-delta, 0)) * var(--gantt-px-per-day));
  width: calc(var(--bar-span) * var(--gantt-px-per-day));
  transform: translateY(-50%);
  height: var(--gantt-bar-height);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.8rem;
  border-radius: var(--gantt-bar-radius);
  background: var(--color-surfaces-bg-elevation-4);
  border: 0.05rem solid var(--color-surfaces-bg-elevation-5);
  box-shadow: 0 10px 15px -3px #0000001a;
  user-select: none;
  z-index: 4;
  // Restored per Phase 1's own note: drag lands in Phase 2 (Task 18), so a grab cursor no longer
  // sits over something ungrabbable. `grabbing` while any gesture (move OR resize) is live —
  // `data-dragging`/`data-resizing` are `Bar`'s own live `useBarDrag` state, not a hardcoded flag.
  cursor: grab;

  &[data-dragging='true'],
  &[data-resizing='start'],
  &[data-resizing='end'] {
    cursor: grabbing;
  }

  // DECISION, not an oversight: `transition: width 0.3s, left 0.3s` (draggable-strip.tsx:133) is
  // deliberately NOT ported. In the old module `Container` sets `width: 100%` and no `left` at
  // all — the bar was positioned by the PARENT's CSS-grid column placement
  // (`gridColumnStart`/`gridColumnEnd` in chart.tsx), so `transition: left` was always inert and
  // `transition: width` only ever fired when the grid cell itself resized. Porting the declaration
  // literally would not restore old behaviour; it would ADD animation that never shipped, because
  // here `left`/`width` are exactly the properties driven by drag and by zoom. In Phase 2 that
  // would put a ~300ms lag between the cursor and the bar on every drag — the old module never had
  // that lag, precisely because its transition was dead. Whoever ships the zoom-transition affordance
  // should add it then, gated off during interaction so dragging still snaps:
  // `&[data-dragging='true'] { transition: none; }`.

  // draggable-strip.tsx:189-197 (minus the nested StripTitle rule, which becomes the `.title`
  // rule below).
  &[data-done='true'] {
    background: var(--color-surfaces-bg-elevation-3);
    border-color: var(--color-surfaces-bg-elevation-4);
  }

  // draggable-strip.tsx:199-210. Gradient stops and border-image are hand-tuned; copied exactly.
  &[data-fade-start='true'] {
    background: linear-gradient(
      90deg,
      rgba(73, 78, 90, 0) 17%,
      rgba(73, 78, 90, 0.34) 60.5%,
      #494e5a 89%
    );
    border: 0.5px solid;
    border-image-source: linear-gradient(90deg, rgba(98, 104, 120, 0) 50%, #626878 100%);
  }

  // draggable-strip.tsx:212-221.
  &[data-fade-end='true'] {
    background: linear-gradient(
      90deg,
      #494e5a 11%,
      rgba(73, 78, 90, 0.62) 49%,
      rgba(49, 52, 60, 0) 83%
    );
    border: 0.5px solid;
    border-image-source: linear-gradient(270deg, rgba(98, 104, 120, 0) 50%, #626878 100%);
  }

  // The 4px resize hit strips, draggable-strip.tsx:223-241. Inert in Phase 1 — Phase 2 attaches
  // the pointer handlers — but the geometry belongs with the rest of the bar's CSS.
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0.4rem;
    background-color: transparent;
    cursor: ew-resize;
    z-index: 5;
  }

  &::before {
    left: -0.2rem;
  }

  &::after {
    right: -0.2rem;
  }

  &:hover {
    border: 0.1rem solid var(--color-texts-medium-contrast);
  }

  // Spec §8's keyboard path (Task 19): a real focus affordance, driven by `Bar`'s own tracked
  // `isFocused` state via `data-focused` — the same data-attribute pattern as `data-dragging`/
  // `data-resizing` (see this file's `:root` note in `bar.tsx`), not `:focus-visible` alone, so a
  // custom `renderers.bar` that never calls `useBarKeyboard` doesn't silently inherit an
  // affordance for a keyboard path it hasn't wired up. No `transition` added here either, for the
  // identical reason given above: `outline` is drawn instantly by every browser without one.
  &[data-focused='true'] {
    outline: 0.2rem solid var(--color-texts-high-contrast);
    outline-offset: 0.1rem;
  }
}

// Task 23. The `aria-describedby` hint and the `role="status"` preview announcement: read by a
// screen reader, never painted. `position: absolute` (not `display: none` / `visibility: hidden`,
// both of which remove the node from the accessibility tree and would make the live region mute)
// and out of `.bar`'s flex flow, so neither node can move the title or the bar's own box.
.srOnly {
  position: absolute;
  width: 0.1rem;
  height: 0.1rem;
  padding: 0;
  margin: -0.1rem;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.8rem;
  color: var(--color-texts-high-contrast);
  user-select: none;

  .bar[data-narrow='true'] & {
    transform: translateX(var(--gantt-px-per-day));
    overflow: visible;
  }

  .bar[data-done='true'] & {
    color: var(--color-texts-middle-contrast);
  }
}
